/**
 * ExpandableSection Component
 *
 * Reusable collapsible section for method detail fields.
 * Features:
 * - Click to expand/collapse
 * - Smooth animation with CSS transitions
 * - Full accessibility (ARIA attributes, keyboard navigation)
 * - Supports HTML content or plain text
 * - Customizable icon
 *
 * @component
 */

import React, { useState, useRef, useEffect } from 'react';
import type { ExpandableSectionProps } from '../types/method-fields';
import { MarkdownContent } from './MarkdownContent';

export const ExpandableSection = React.forwardRef<HTMLDivElement, ExpandableSectionProps>(
  (
    {
      title,
      content,
      icon,
      defaultExpanded = false,
      isHtml = false,
      isMarkdown = false,
      testId,
      children,
    },
    ref
  ) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const contentRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<number | undefined>(defaultExpanded ? undefined : 0);

    // Generate unique ID for accessibility
    const contentId = `section-content-${title.toLowerCase().replace(/\s+/g, '-')}`;
    const buttonId = `section-button-${title.toLowerCase().replace(/\s+/g, '-')}`;

    // Handle expand/collapse animation
    useEffect(() => {
      if (!contentRef.current) return;

      if (isExpanded) {
        const contentHeight = contentRef.current.scrollHeight;
        setHeight(contentHeight);
      } else {
        setHeight(0);
      }
    }, [isExpanded]);

    const handleToggle = (): void => {
      setIsExpanded(!isExpanded);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
      // Support Enter and Space keys for accessibility
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleToggle();
      }
    };

    return (
      <div
        ref={ref}
        className="expandable-section border border-zinc-200 dark:border-zinc-700 rounded-xl mb-4 overflow-hidden transition-all duration-200"
        data-testid={testId}
      >
        {/* Header Button */}
        <button
          id={buttonId}
          type="button"
          className="expandable-section-header w-full flex items-center gap-3 p-4 text-left bg-zinc-50 dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-inset"
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          aria-expanded={isExpanded}
          aria-controls={contentId}
        >
          {/* Icon (optional) */}
          {icon && <span className="expandable-section-icon text-lg shrink-0">{icon}</span>}

          {/* Title */}
          <span className="expandable-section-title flex-1 font-semibold text-zinc-900 dark:text-zinc-100">
            {title}
          </span>

          {/* Chevron Icon */}
          <svg
            className={`expandable-section-chevron w-5 h-5 text-zinc-500 dark:text-zinc-400 transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* Content Area */}
        <div
          ref={contentRef}
          id={contentId}
          role="region"
          aria-labelledby={buttonId}
          className="expandable-section-content overflow-hidden transition-all duration-200"
          style={{
            height: height !== undefined ? `${height}px` : 'auto',
          }}
        >
          <div className="p-4 border-t border-zinc-200 dark:border-zinc-700">
            {/* Render children if provided, otherwise render content based on type */}
            {children ? (
              children
            ) : isMarkdown || isHtml ? (
              <MarkdownContent content={content} />
            ) : (
              <p className="text-zinc-700 dark:text-zinc-300 whitespace-pre-line">{content}</p>
            )}
          </div>
        </div>
      </div>
    );
  }
);

ExpandableSection.displayName = 'ExpandableSection';
